import { useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';

interface SessionExpiryDialogProps {
  readonly remainingMs: number;
  onStaySignedIn: () => void;
}

/** MM:SS, as the dialog copy fixes it (proweb-18 AC-002). */
function formatCountdown(remainingMs: number): string {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Modal warning shown shortly before the inactivity sign-out (proweb-18). The overlay covers the page
 * and focus is held on the one button, so the page underneath cannot be used until the provider either
 * stays signed in or the countdown ends.
 */
export function SessionExpiryDialog({
  remainingMs,
  onStaySignedIn,
}: SessionExpiryDialogProps): React.JSX.Element {
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    buttonRef.current?.focus();
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="session-expiry-title"
        aria-describedby="session-expiry-body"
        className="border-line w-full max-w-md rounded-xl border bg-surface p-6 shadow-2xl"
      >
        <h2 id="session-expiry-title" className="text-brand-600 text-lg font-bold">
          Session Expiring Soon
        </h2>
        <div id="session-expiry-body" className="text-ink mt-3 space-y-2 text-sm">
          <p>
            Your session has been inactive and will expire in{' '}
            <strong className="tabular-nums">{formatCountdown(remainingMs)}</strong> due to
            inactivity.
          </p>
          <p>Select Stay Signed In to continue your session, or you will be automatically signed out.</p>
        </div>
        <div className="mt-5 flex justify-end">
          <Button
            ref={buttonRef}
            type="button"
            onClick={onStaySignedIn}
            onKeyDown={(event): void => {
              // The only focusable element: Tab must not escape to the page behind the overlay.
              if (event.key === 'Tab') {
                event.preventDefault();
              }
            }}
          >
            Stay Signed In
          </Button>
        </div>
      </div>
    </div>
  );
}
